"use client";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useRef, useState } from "react";
import { cn } from "@/lib/cn";
import type { NavItem } from "@/lib/content/types";
import { TopbarMenuLink } from "./TopbarMenu";
import { ProfileAvatar } from "@/components/profile/ProfileAvatar";
import { isCenteredNav, isGlassHeader, usesTileNav, usesTopbarNav } from "@/lib/site";
import { EASE, spring } from "@/lib/motion";
import { Icon } from "@/components/ui/Icon";
import { Tooltip } from "@/components/ui/primitives";
import { useContent } from "./ContentContext";
import { LanguageSelector } from "./LanguageSelector";
import { useShell } from "./ShellContext";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { RewardsPanel, useRewardsIndicator, useRewardStatusToasts } from "./RewardsPanel";
import { useDismiss } from "@/components/ui/useDismiss";
import { useBalances, useIdentity, useSignOut } from "@/lib/api/session";
import { formatAmount, isDollarPegged } from "@/lib/format/amount";
import { AnimatedAmount } from "@/components/ui/AnimatedAmount";

export function TopBar({
  onWallet,
  onChat,
  onRail,
  onMenu,
  menuOpen,
  railOpen,
  chatOpen,
  siteName,
  casinoNav,
}: {
  onWallet: () => void;
  onChat: () => void;
  onRail: () => void;
  /* Combined: the menu button before the logo, opening the side menu as a
     drawer (desktop; a phone has the tab bar's). */
  onMenu?: () => void;
  menuOpen?: boolean;
  railOpen: boolean;
  chatOpen: boolean;
  siteName: string;
  casinoNav: NavItem[];
}) {
  const { branding, flags, notifications, nav } = useContent();
  const centered = isCenteredNav(flags.navLayout);
  const topbar = usesTopbarNav(flags.navLayout);
  const centerActions = flags.headerActions === "Center";
  const pathname = usePathname();
  const { t } = useI18n();

  const [bellOpen, setBellOpen] = useState(false);
  const router = useRouter();
  const [search, setSearch] = useState("");

  const { openSignIn } = useShell();
  const { identity, isSignedIn, isPending } = useIdentity();
  const balances = useBalances(isSignedIn);
  const signOut = useSignOut();

  /* The gift button, on the kit's dropdown-rewards frame: a dot when something
     is claimable or unseen, the panel on click, and the status watcher that
     turns polled reward changes into snackbars — mounted here because the top
     bar is on every page, so a change is announced wherever the player is. */
  const [rewardsOpen, setRewardsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  /* Each popover closes on a click outside it or Escape, like any menu. The
     ref wraps button and panel together, so the toggle itself never counts
     as outside. */
  const profileRef = useRef<HTMLSpanElement>(null);
  const rewardsRef = useRef<HTMLSpanElement>(null);
  const bellRef = useRef<HTMLSpanElement>(null);
  useDismiss(profileRef, profileOpen, () => setProfileOpen(false));
  useDismiss(rewardsRef, rewardsOpen, () => setRewardsOpen(false));
  useDismiss(bellRef, bellOpen, () => setBellOpen(false));
  const rewardsIndicator = useRewardsIndicator(isSignedIn);
  useRewardStatusToasts(isSignedIn);


  const unread = isSignedIn ? notifications.filter((n) => n.unread).length : 0;
  /* A wide image logo already carries the name, so it stands alone; without
     an uploaded image there is nothing wide to show, and the built-in mark
     with its name stands in. */
  const wideLogo = branding.logoStyle === "Wide image" && Boolean(branding.logoUrl);
  const wordmark = !wideLogo && branding.wordmark !== false;

  /* One figure in the pill: the first bankroll's balance, the rest are in
     the wallet. Signed out, the pill becomes the way in. */
  const primary = balances.data?.at(0);

  const bell = (
    <>
          {flags.notificationsEnabled && isSignedIn && (
            <span className="relative block" ref={bellRef}>
              <TopIcon
                name="bell"
                label={t("topbar.notifications")}
                onClick={() => setBellOpen((o) => !o)}
                active={bellOpen}
                expanded={bellOpen}
              />
              {unread > 0 && (
                <span className="glow-highlight absolute right-0 top-0 h-[6px] w-[6px] rounded-full bg-highlight" />
              )}
              <AnimatePresence>
                {bellOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.2, ease: EASE }}
                    className="modal-shadow absolute right-0 top-[46px] z-50 w-[300px] overflow-hidden rounded-xl border border-border bg-elevation-2"
                  >
                    <p className="border-b border-border px-[16px] py-[12px] text-[12px] font-bold uppercase leading-[1.6] text-fg-subtle">
                      Notifications
                    </p>
                    {!isSignedIn ? (
                      <p className="px-[16px] py-[24px] text-center text-[12px] leading-[1.6] text-fg-subtle">
                        Notifications are tied to your account. Sign in to see them.
                      </p>
                    ) : notifications.length === 0 ? (
                      <p className="px-[16px] py-[24px] text-center text-[12px] leading-[1.6] text-fg-subtle">Nothing new.</p>
                    ) : (
                      <ul>
                        {notifications.map((n) => (
                          <li key={n.title}>
                            <Link
                              href={n.href}
                              onClick={() => setBellOpen(false)}
                              className="flex gap-[12px] px-[16px] py-[12px] transition-colors hover:bg-elevation-3"
                            >
                              <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-md bg-elevation-4 text-fg">
                                <Icon name={n.icon} size={18} />
                              </span>
                              <span className="min-w-0">
                                <span className="flex items-center gap-[6px] text-[12px] font-bold leading-[1.6]">
                                  {n.title}
                                  {n.unread && <span className="h-[6px] w-[6px] rounded-full bg-highlight" />}
                                </span>
                                <span className="mt-[2px] block text-[12px] leading-[1.6] text-fg-subtle">{n.body}</span>
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </span>
          )}

    </>
  );

  return (
    <header
      data-tone={flags.headerStyle === "Brand" ? "brand" : undefined}
      className={cn(
        "sticky z-40 flex shrink-0 items-center gap-[12px]",
        flags.headerStyle === "Floating"
          ? /* Feature flags -> Top bar -> Floating: a rounded glass capsule
               held off the top and sides, as wide as the page column. */
            "top-[calc(8px+var(--tg-inset-top))] mx-[8px] mt-[8px] h-[52px] rounded-[14px] bg-black/50 pl-[12px] pr-[6px] backdrop-blur-md lg:top-[calc(16px+var(--tg-inset-top))] lg:mx-auto lg:mt-[16px] lg:w-[calc(100%-48px)] lg:max-w-[1144px] lg:rounded-[16px] lg:pl-[22px]"
          : cn(
              "top-[var(--tg-inset-top)] h-[56px] px-[12px] lg:h-20",
              /* Boxed with no side menu, the bar's content starts and ends
                 where the page column does. */
              topbar && flags.pageWidth === "Centered" ? "lg:px-[max(16px,calc((100%-1144px)/2+24px))]" : "lg:px-[16px]",
              /* Feature flags -> Top bar -> Glass: translucent over the page. */
              flags.headerStyle === "Glass"
                ? "bg-canvas/40 backdrop-blur-md"
                : flags.headerStyle === "Brand"
                  ? /* Feature flags -> Top bar -> Brand: the bar in the brand
                       colour; globals.css re-tones what sits on it. */
                    "bg-[var(--page-brand)] shadow-[0_2px_8px_rgb(0_0_0/0.18)]"
                  : "border-b border-border bg-elevation-1",
            ),
      )}
    >
      {!railOpen && (
        <button
          onClick={onRail}
          aria-label={t("topbar.expandNav")}
          className="hidden h-[38px] w-[38px] shrink-0 place-items-center rounded-lg bg-elevation-3 text-fg transition-colors hover:bg-elevation-4 lg:grid"
        >
          <Icon name="menu" size={20} />
        </button>
      )}

      {onMenu && (
        <button
          type="button"
          onClick={onMenu}
          aria-label={t("topbar.expandNav")}
          aria-expanded={menuOpen}
          className="hidden h-[38px] w-[38px] shrink-0 place-items-center rounded-lg text-fg transition-colors hover:bg-fg/[0.1] lg:grid"
        >
          <Icon name={menuOpen ? "close" : "menu"} size={22} />
        </button>
      )}

      {/* The Centered layout seats the lockup on the bar's centre line, both
          ways: pinned to the full height of the bar and centred across it,
          so it reads as the page's mark rather than one more control. The
          compact bar keeps it at the left, where the controls leave no room. */}
      <Link
        href="/"
        className={cn(
          "group flex h-[38px] shrink-0 items-center gap-[12px]",
          centered && "lg:absolute lg:inset-y-0 lg:left-1/2 lg:h-auto lg:-translate-x-1/2 lg:justify-center",
        )}
      >
        {wideLogo ? (
          <motion.img
            whileHover={{ scale: 1.03 }}
            transition={spring}
            src={branding.logoUrl}
            alt={siteName}
            className="h-[34px] w-auto max-w-[190px] shrink-0 object-contain lg:h-[52px] lg:max-w-[320px]"
          />
        ) : branding.logoUrl ? (
          <motion.img
            whileHover={{ rotate: -8, scale: 1.06 }}
            transition={spring}
            src={branding.logoUrl}
            alt={wordmark ? "" : siteName}
            className="h-[32px] w-auto max-w-[168px] shrink-0 object-contain"
          />
        ) : (
          <motion.span
            whileHover={{ rotate: -8, scale: 1.06 }}
            transition={spring}
            className="grid h-[32px] w-[32px] place-items-center overflow-hidden rounded-md bg-white"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M4 4h7l9 8-9 8H4l9-8z" fill="var(--color-brand)" />
              <path d="M4 4h7l-4 8 4 8H4z" fill="var(--color-brand-subtle)" />
            </svg>
          </motion.span>
        )}
        {wordmark && <span className="text-[18px] font-extrabold tracking-[-0.54px]">{siteName}</span>}
        {branding.demoBadge && (
          <span className="shrink-0 rounded-sm bg-brand px-[6px] py-[2px] text-[9px] font-extrabold uppercase leading-[1.5] tracking-[0.12em] text-fg-on-primary">
            Demo
          </span>
        )}
      </Link>

      {/* The Topbar layout has no side menu: its links sit here, beside the
          logo, an icon and a label each (desktop; phones keep the drawer). */}
      {topbar && (
        <nav aria-label="Main" className="hidden min-w-0 items-center gap-[4px] overflow-hidden lg:flex">
          {nav.primary.map((item) => (
            <TopbarMenuLink
              key={`${item.label}-${item.href}`}
              item={item}
              active={item.href === "/" ? pathname === "/" : pathname.startsWith(item.href)}
              casinoNav={casinoNav}
            />
          ))}
        </nav>
      )}

      {/* Feature flags -> Search field in the top bar: the field across the
          middle of the bar, opening the full game list on what was typed. */}
      {flags.topSearch && (
        <form
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            const query = search.trim();
            router.push(query ? `/c/all?q=${encodeURIComponent(query)}` : "/c/all");
          }}
          /* Centered: the lockup holds the bar's centre line, so the field
             keeps to the left half, clear of it (StarBet's ran under it). */
          className={cn(
            "hidden min-w-0 flex-1 justify-center lg:flex",
            centered && "lg:max-w-[calc(50%-160px)] lg:justify-start",
          )}
        >
          <label className="flex h-[40px] w-full max-w-[460px] items-center gap-[8px] rounded-rounded border border-border bg-elevation-2 px-[16px] transition-colors focus-within:border-brand">
            <Icon name="search" size={16} className="shrink-0 text-fg-subtle" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("topbar.searchGames")}
              aria-label={t("topbar.searchGames")}
              className="h-full min-w-0 flex-1 bg-transparent text-[13px] font-bold leading-[1.6] text-fg outline-none placeholder:text-fg-subtle"
            />
          </label>
        </form>
      )}

      <div className={cn("flex min-w-0 items-center justify-end gap-[8px]", flags.topSearch ? "flex-1 lg:flex-none" : "flex-1", flags.topSearch && centered && "lg:ml-auto")}>
        {/* The selector lives at the bottom of the rail (and the mobile
            drawer, which mounts the rail). The Tile layout has no rail, so
            only there does it stay in the bar. */}
        {(usesTileNav(flags.navLayout) || topbar) && (
          <span className="hidden lg:block">
            <LanguageSelector />
          </span>
        )}

        <Tooltip content={t("topbar.searchGames")} side="bottom">
          <Link href="/c/all" aria-label={t("topbar.searchGames")} className={cn("block", flags.topSearch && "lg:hidden")}>
            <TopIcon name="search" label={t("topbar.searchGames")} />
          </Link>
        </Tooltip>

        {isSignedIn && identity && (
          <span className="relative hidden lg:block" ref={profileRef}>
            <motion.button
      data-pill=""
              onClick={() => setProfileOpen((open) => !open)}
              aria-label={t("topbar.accountMenu")}
              aria-haspopup="menu"
              aria-expanded={profileOpen}
              whileHover={{ scale: 1.07 }}
              whileTap={{ scale: 0.95 }}
              transition={spring}
              className="block"
            >
              <ProfileAvatar accountId={identity.id} name={identity.nickname} size={38} />
            </motion.button>
            <AnimatePresence>
              {profileOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2, ease: EASE }}
                  className="modal-shadow absolute right-0 top-[46px] z-50 w-[220px] overflow-hidden rounded-xl border border-border bg-elevation-2"
                >
                  {/* The whitelabel's ProfileButton menu: the identity row into
                      the account page, the account destinations, sign out. */}
                  <Link
                    href="/account"
                    onClick={() => setProfileOpen(false)}
                    className="flex items-center gap-[10px] border-b border-border px-[14px] py-[12px] transition-colors hover:bg-elevation-3"
                  >
                    <ProfileAvatar accountId={identity.id} name={identity.nickname} size={30} />
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-bold leading-[1.5]">{identity.nickname}</span>
                      <span className="block text-[11px] leading-[1.5] text-fg-subtle">{t("topbar.viewAccount")}</span>
                    </span>
                  </Link>
                  <ul className="py-[4px]">
                    {(
                      [
                        { label: t("topbar.myAccount"), href: "/account", icon: "user" },
                        { label: t("topbar.bets"), href: "/account?tab=Bets", icon: "card" },
                        { label: t("topbar.transactions"), href: "/account?tab=Transactions", icon: "book" },
                        { label: t("topbar.rewards"), href: "/account?tab=Rewards", icon: "gift" },
                      ] as const
                    ).map((item) => (
                      <li key={item.label}>
                        <Link
                          href={item.href}
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-[10px] px-[14px] py-[8px] text-[12px] font-bold leading-[1.6] text-fg-subtle transition-colors hover:bg-elevation-3 hover:text-fg"
                        >
                          <Icon name={item.icon} size={16} />
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => {
                      setProfileOpen(false);
                      signOut.mutate();
                    }}
                    className="flex w-full items-center gap-[10px] border-t border-border px-[14px] py-[10px] text-left text-[12px] font-bold leading-[1.6] text-error-fg transition-colors hover:bg-elevation-3"
                  >
                    <Icon name="external" size={16} />
                    {t("topbar.signOut")}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </span>
        )}

        {/* Feature flags -> Top bar actions -> Center: rewards, the way in
            (or the wallet) and notifications gathered in the middle of the
            bar over a soft glow (thrill.com's). Otherwise, and on a phone,
            they sit in line at the right: `contents` leaves the row as it
            always was. */}
        <div
          className={
            centerActions
              ? "flex items-center gap-[8px] lg:absolute lg:left-1/2 lg:top-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2"
              : "contents"
          }
        >
          {centerActions && (
            <span
              aria-hidden
              className="pointer-events-none absolute left-1/2 top-1/2 -z-10 hidden h-[110px] w-[380px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/15 blur-[48px] lg:block"
            />
          )}
          <Tooltip content={t("topbar.rewards")} side="bottom">
            {isSignedIn ? (
              <span className="relative hidden lg:block" ref={rewardsRef}>
                <TopIcon
                  name="gift"
                  label={t("topbar.rewards")}
                  onClick={() => {
                    setRewardsOpen((open) => !open);
                    if (!rewardsOpen) rewardsIndicator.markSeen();
                  }}
                  active={rewardsOpen}
                  expanded={rewardsOpen}
                />
                {rewardsIndicator.active && (
                  <span className="glow-highlight absolute right-0 top-0 h-[6px] w-[6px] rounded-full bg-highlight" />
                )}
                <AnimatePresence>
                  {rewardsOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2, ease: EASE }}
                      className="modal-shadow absolute right-0 top-[46px] z-50 w-[360px] overflow-hidden rounded-xl border border-border bg-elevation-1"
                    >
                      <RewardsPanel onClose={() => setRewardsOpen(false)} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </span>
            ) : (
              <Link href="/rewards" className="hidden lg:block">
                <TopIcon name="gift" label={t("topbar.rewards")} />
              </Link>
            )}
          </Tooltip>

          {!centerActions && bell}
          {flags.walletEnabled &&
            (isSignedIn ? (
              <>
                <motion.button
        data-pill=""
                  onClick={onWallet}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.97 }}
                  transition={spring}
                  className="flex h-[38px] shrink-0 items-center gap-[8px] rounded-lg bg-brand px-[12px] text-[12px] font-bold leading-[1.6] text-fg-on-primary transition-colors duration-200 hover:bg-brand-bold"
                >
                  <Icon name="wallet" size={16} />
                  {balances.isPending ? (
                    <span className="h-3 w-12 animate-pulse rounded bg-white/25" />
                  ) : balances.isError ? (
                    <span className="tabular-nums">—</span>
                  ) : primary ? (
                    <span className="tabular-nums">
                      {isDollarPegged(primary.bankroll.token.symbol) ? "$" : ""}
                      <AnimatedAmount amount={primary.amount} token={primary.bankroll.token} />
                      {isDollarPegged(primary.bankroll.token.symbol) ? "" : ` ${primary.bankroll.token.symbol}`}
                    </span>
                  ) : (
                    <span className="tabular-nums">0.00</span>
                  )}
                </motion.button>
              </>
            ) : (
              <>
                {/* With a Register button beside it, Sign in steps back to an
                    outline and Register takes the brand fill. */}
                <motion.button
        data-pill=""
                  data-glow={flags.registerButton ? undefined : ""}
                  onClick={() => openSignIn()}
                  disabled={isPending}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.97 }}
                  transition={spring}
                  className={cn(
                    "flex h-[38px] shrink-0 items-center gap-[8px] rounded-lg px-[12px] text-[12px] font-bold leading-[1.6] transition-colors duration-200 disabled:opacity-70",
                    /* On a phone the pair would crowd the logo; Register
                       alone opens the same sign-in. */
                    flags.registerButton
                      ? flags.signInStyle === "Filled"
                        ? "hidden bg-elevation-4 text-fg hover:bg-elevation-3 sm:flex"
                        : flags.signInStyle === "Text"
                          ? "hidden bg-transparent text-fg hover:bg-elevation-3 sm:flex"
                          : "hidden border border-brand bg-transparent text-fg hover:bg-elevation-3 sm:flex"
                      : isGlassHeader(flags.headerStyle)
                        ? /* On glass, the way in is glass too: the brand, half clear. */
                          "bg-brand/50 text-fg backdrop-blur-sm hover:bg-brand/70"
                        : "bg-brand text-fg-on-primary hover:bg-brand-bold",
                  )}
                >
                  {!flags.registerButton && <Icon name="user" size={16} />}
                  {t("topbar.signIn")}
                </motion.button>
                {flags.registerButton && (
                  <motion.button
        data-pill=""
                    data-glow=""
                    onClick={() => openSignIn()}
                    disabled={isPending}
                    whileHover={{ y: -1 }}
                    whileTap={{ scale: 0.97 }}
                    transition={spring}
                    className={cn(
                      "flex h-[38px] shrink-0 items-center gap-[8px] rounded-lg bg-brand px-[12px] text-[12px] font-bold leading-[1.6] text-fg-on-primary transition-colors duration-200 hover:bg-brand-bold disabled:opacity-70",
                      /* Centred, Register glows in its own colour. */
                      centerActions && "[filter:drop-shadow(0_0_10px_color-mix(in_srgb,var(--color-brand)_40%,transparent))]",
                    )}
                  >
                    {t("topbar.register")}
                  </motion.button>
                )}
              </>
            ))}

          {centerActions && bell}
        </div>

        {flags.chatEnabled && isSignedIn && (
          <TopIcon name="chat" label="Toggle chat" onClick={onChat} active={chatOpen} className="hidden xl:grid" />
        )}
      </div>
    </header>
  );
}

function TopIcon({
  name,
  label,
  onClick,
  active,
  expanded,
  className,
}: {
  name: string;
  label: string;
  onClick?: () => void;
  active?: boolean;
  expanded?: boolean;
  className?: string;
}) {
  /* On a glass top bar the icon buttons are soft glass as well. */
  const soft = isGlassHeader(useContent().flags.headerStyle);
  return (
    <motion.button
      data-pill=""
      onClick={onClick}
      aria-label={label}
      aria-expanded={expanded}
      whileHover={{ scale: 1.07 }}
      whileTap={{ scale: 0.94 }}
      transition={spring}
      className={cn(
        "grid h-[38px] w-[38px] shrink-0 place-items-center rounded-lg transition-colors duration-200",
        soft
          ? active
            ? "bg-fg/[0.16] text-fg"
            : "bg-fg/[0.06] text-fg hover:bg-fg/[0.12]"
          : active
            ? "bg-elevation-3 text-fg"
            : "bg-elevation-2 text-fg-muted hover:bg-elevation-3 hover:text-fg",
        className,
      )}
    >
      <Icon name={name} size={20} />
    </motion.button>
  );
}
